import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // Alles in einer Zeile
import GlobalLayout from './GlobalLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getSecureMediaUrl } from '../utils/mediaUtils';
import { appConfig } from '../config'; 
import './ElementCreator.css';
import { generateScriptSuggestions, addElementToCollection } from '../services/apiService';
// @mmote/niimbluelib wird dynamisch geladen (kein SSR-Crash)


// --- SECURITY HELPER ---
const sanitizeInput = (str) => {
    if (!str) return '';
    // Entfernt HTML Tags (<...>)
    let clean = str.replace(/<[^>]*>?/gm, '');
    return clean.trim();
};

// --- SCRIPT PREFIX STRIPPER ---
// Das LLM liefert manchmal Prefixe wie "Option 3 (Concise): " oder "Option 2 - Detailed: "
// Diese werden vor Anzeige und Übernahme entfernt.
const stripScriptPrefix = (text) => {
  if (!text) return text;
  // Matcht: "Option 3:", "Option 3 (Concise):", "Option 3 - Concise:", "Option 3 – Concise:"
  return text.replace(/^Option\s+\d+\s*(\([^)]*\)|[\-–][^:]*)?:\s*/i, '').trim();
};

// --- DOCUMENT HELPER ---
const DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const getDocumentIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType === 'application/vnd.ms-powerpoint' || mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return '📊';
    if (mimeType === 'text/plain') return '📝';
    if (mimeType?.includes('word') || mimeType?.includes('msword')) return '📃';
    return '📁';
};

const getDocumentTypeName = (mimeType) => {
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType === 'application/vnd.ms-powerpoint') return 'PPT';
    if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return 'PPTX';
    if (mimeType === 'text/plain') return 'TXT';
    if (mimeType === 'application/msword') return 'DOC';
    if (mimeType?.includes('wordprocessingml')) return 'DOCX';
    return 'Dokument';
};

const ElementCreator = () => {
  const navigate = useNavigate();
  const location = useLocation(); 

  // 1. NEUE STATES HINZUFÜGEN (Am Anfang der Komponente)
  const [showScriptSelection, setShowScriptSelection] = useState(false); // Steuert, ob wir im Zwischenschritt sind
  const [scriptOptions, setScriptOptions] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptRetryCount, setScriptRetryCount] = useState(0);
  const [isLoadingScripts, setIsLoadingScripts] = useState(false);

  // NEU: Upload-Modus State (Tabs in Step 2)
  const [uploadMode, setUploadMode] = useState('media'); // 'media' | 'document'

  const { t, currentLanguage } = useLanguage();
  const { user, token, hasFeature, getElement } = useAuth();

  // Access-Guard: nur wenn features.element_creator aktiv
  useEffect(() => {
    if (!hasFeature('element_creator')) {
      navigate('/', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Admins mit admin_panel-Feature: Toggle ob Inhalt der "Allgemein"-Gruppe zugeordnet wird
  const isAdminUser = hasFeature('admin_panel');
  const [assignToGeneral, setAssignToGeneral] = useState(true);

  // Wizard Steps: 
  // 1: Name
  // 2: Media (Upload/Camera) or Document
  // 3: Description
  // 4: Review & Submit
  // 5: Processing (Loading)
  const [step, setStep] = useState(1);
  
  // Form Data State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    mediaFile: null,
    mediaType: null, // 'video' or 'image'
    useExactText: false,
    isReactionVideo: false,
    useVideoTranscription: false,  // NEU: Ton des Videos transkribieren und als Voiceover nutzen
    mediaPreviewUrl: null,
    // NEU: Dokument-Felder
    documentFile: null,
    documentFileName: null,
    documentMimeType: null,
    isDocumentMode: false,  // true wenn Dokument statt Foto/Video
  });

  // --- NEU: INTELLIGENTE SCHLIESSEN-FUNKTION ---
  const handleClose = () => {
      // Prüfen, ob eine Rücksprung-Adresse im State mitgegeben wurde
      if (location.state && location.state.returnUrl) {
          navigate(location.state.returnUrl);
      } else {
          // Fallback: Wenn User direkt kam oder kein State da ist -> Dashboard
          navigate('/profile');
      }
  };

  // --- API CALL ÜBER SERVICE ---
  const fetchScriptSuggestions = async () => {
    setIsLoadingScripts(true);
    setError('');
    
    try {
        const data = await generateScriptSuggestions(
            formData.name, 
            formData.description, 
            currentLanguage
        );

        if (data && data.options && Array.isArray(data.options)) {
            let finalOptions = data.options;

            // --- NEU: Für Gruppe 0 Original-Text am ENDE anfügen ---
            if (hasFeature('super_admin')) {
                // Wir nehmen die KI-Vorschläge und hängen das Original hinten an
                finalOptions = [...finalOptions, formData.description];
            }
            // -------------------------------------------------------

            setScriptOptions(finalOptions);
        } else {
            setScriptOptions([]);
            console.warn("Keine Optionen erhalten", data);
        }
    } catch (e) {
        console.error("Fehler beim Laden der Skripte:", e);
        setError(t('errorLoadingScripts') || "Fehler beim Laden der Vorschläge.");
    } finally {
        setIsLoadingScripts(false);
    }
  };

  const [error, setError] = useState('');
  // Separater State für inline-Validierungsfehler innerhalb eines Steps
  // (wird NICHT als globaler Error-Screen gerendert)
  const [stepError, setStepError] = useState('');
  const [uploadErrorType, setUploadErrorType] = useState(null); // null | 'timeout' | 'general'
  const [isProcessing, setIsProcessing] = useState(false);
  // Neuer State für das Ergebnis nach dem Erstellen
  const [createdElementData, setCreatedElementData] = useState(null);
  // Video-Bereitschafts-State für Success-Screen:
  // null = noch nicht geprüft, false = FFmpeg läuft noch, true = bereit
  const [videoReady, setVideoReady] = useState(null);
  const videoReadyPollRef = useRef(null);

  // --- VIDEO READINESS POLL ---
  // Prüft alle 5s ob stream_0.m3u8 (erster HLS-Segment-Index, nur von FFmpeg erzeugt)
  // tatsächlich erreichbar ist. master.m3u8 existiert bereits synchron — es ist
  // KEIN zuverlässiges Signal für Abspielbereitschaft.
  //
  // Prüfmethode: HEAD auf stream_0.m3u8 via getSecureMediaUrl() — diese Funktion
  // fügt die korrekte Media-Proxy-Auth hinzu (gleiche Methode wie ElementFeed/ElementPage).
  //   → 200/206: FFmpeg fertig, Datei existiert → Button freischalten
  //   → 404:     FFmpeg läuft noch, Datei fehlt  → weiter warten
  //   → 401:     Auth-Problem (sollte nicht auftreten) → weiter warten
  // Timeout nach 25 Versuchen (~2 min).
  useEffect(() => {
    if (!createdElementData?.videoUrl) return;
    if (createdElementData?.pending_approval) return;

    // stream_0.m3u8 = erster Sprach-Track, wird ausschließlich von FFmpeg geschrieben
    const masterUrl   = createdElementData.videoUrl;
    const rawStreamUrl = masterUrl.replace('master.m3u8', 'stream_0.m3u8');
    const streamUrl    = getSecureMediaUrl(rawStreamUrl); // Media-Proxy-Auth hinzufügen

    setVideoReady(null); // Spinner anzeigen
    let attempts = 0;
    const MAX = 25;

    videoReadyPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(streamUrl, { method: 'HEAD' });
        if (res.ok) {
          // 200/206: Datei existiert → FFmpeg fertig
          setVideoReady(true);
          clearInterval(videoReadyPollRef.current);
          videoReadyPollRef.current = null;
          return;
        }
        // 404: Datei fehlt noch → weiter warten (kein setVideoReady(false) nötig)
      } catch (_) { /* Netzwerkfehler — nächster Versuch */ }

      if (attempts >= MAX) {
        // Timeout: nach ~2 min Button trotzdem freigeben
        setVideoReady(true);
        clearInterval(videoReadyPollRef.current);
        videoReadyPollRef.current = null;
      } else {
        setVideoReady(false);
      }
    }, 5000);

    return () => {
      if (videoReadyPollRef.current) {
        clearInterval(videoReadyPollRef.current);
        videoReadyPollRef.current = null;
      }
    };
  }, [createdElementData?.videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- PROGRESS BAR STATES ---
  const [progressValue, setProgressValue] = useState(0);   // 0–100
  const [progressStageIdx, setProgressStageIdx] = useState(0);

  // --- NIIMBOT DRUCKER STATES ---
  const [printerClient, setPrinterClient] = useState(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printStatus, setPrintStatus] = useState(''); // 'idle' | 'connecting' | 'printing' | 'done' | 'error'
  const printCanvasRef = useRef(null);
  
  // Refs
  const fileInputRef = useRef(null);
  const documentFileInputRef = useRef(null); // NEU: Ref für Dokument-Upload
  const videoPreviewRef = useRef(null);

  // Config-Switch simulieren (oder aus appConfig importieren)
// Runtime Config: Prüft erst window.HEJVI_SETTINGS (Server-Datei), dann appConfig (Build-Datei)
const ENABLE_SCRIPT_REVIEW = (window.HEJVI_SETTINGS && window.HEJVI_SETTINGS.enableModerationReview !== undefined)
    ? window.HEJVI_SETTINGS.enableModerationReview
    : (appConfig.enableModerationReview !== false);


  // Cleanup Object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (formData.mediaPreviewUrl) {
        URL.revokeObjectURL(formData.mediaPreviewUrl);
      }
    };
  }, [formData.mediaPreviewUrl]);

  // --- PROGRESS BAR LOGIK ---
  // Das Frontend schickt beim Submit einen progress_token mit.
  // Während der POST /create-element läuft, pollt ein Interval alle 800ms
  // GET /element-progress?token={token} und aktualisiert Bar + Label.
  const [progressToken]  = useState(() => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  const progressPollRef    = useRef(null);
  const simStartRef        = useRef(null);  // Timestamp für asymptotische Simulation
  const simIntervalRef     = useRef(null);  // Simulation-Interval-Handle
  const lastRealPctRef     = useRef(0);     // Letzter echter Backend-Wert

  useEffect(() => {
    // ── Hilfsfunktion: alle Intervals stoppen ─────────────────────────────
    const stopAll = () => {
      if (progressPollRef.current) { clearInterval(progressPollRef.current); progressPollRef.current = null; }
      if (simIntervalRef.current)  { clearInterval(simIntervalRef.current);  simIntervalRef.current  = null; }
    };

    if (!isProcessing) {
      stopAll();
      if (progressValue > 0) {
        setProgressValue(95);
        // Stage-Idx auf vorletzten (Speichern) setzen
        const lastStageIdx = formData.isDocumentMode ? 5 : 4;
        setProgressStageIdx(lastStageIdx);
        const tid = setTimeout(() => {
          setProgressValue(0);
          setProgressStageIdx(0);
          lastRealPctRef.current = 0;
        }, 700);
        return () => clearTimeout(tid);
      }
      return;
    }

    // ── Stage-Ankerpunkte je Modus ────────────────────────────────────────
    //
    // Jede Stage: { pct: Ziel-%, duration: ms bis zum nächsten Anker, stageIdx }
    // Die Simulation springt nicht — sie interpoliert linear zwischen Ankern.
    //
    // Dokument-Modus (document_mode=1):
    //   VideoBuilder: 2→8(mod)→32(text)→48(img)→62(summary)→68(transl)→88(audio×10)→92%
    //   Typische Gesamtdauer: 90–180s
    //
    // Video-Modus (document_mode=0):
    //   VideoBuilder: 2→8(mod)→25(script,opt)→40(transl)→50-80(audio×10)→88(db)→92%
    //   Typische Gesamtdauer: 60–120s

    const isDoc = formData.isDocumentMode;

    const docStages = [
      { pct:  2, duration:  4000, stageIdx: 0 }, // Hochladen
      { pct:  8, duration:  5000, stageIdx: 0 }, // Moderation
      { pct: 32, duration: 18000, stageIdx: 1 }, // Text extrahieren
      { pct: 48, duration: 12000, stageIdx: 2 }, // Seiten analysieren
      { pct: 62, duration: 22000, stageIdx: 3 }, // Zusammenfassung (LLM)
      { pct: 68, duration:  8000, stageIdx: 3 }, // Übersetzungen
      { pct: 88, duration: 75000, stageIdx: 4 }, // Voiceover × 10 Sprachen (längste Phase)
      { pct: 92, duration: 10000, stageIdx: 5 }, // DB + Vektorisierung
    ];

    const videoStages = [
      { pct:  2, duration:  3000, stageIdx: 0 }, // Hochladen
      { pct:  8, duration:  4000, stageIdx: 0 }, // Moderation
      { pct: 25, duration: 20000, stageIdx: 1 }, // Skript-Generierung / Verarbeiten
      { pct: 40, duration:  8000, stageIdx: 2 }, // Übersetzungen
      { pct: 80, duration: 60000, stageIdx: 3 }, // Voiceover × 10 Sprachen
      { pct: 92, duration:  8000, stageIdx: 4 }, // DB + Speichern
    ];

    const stages    = isDoc ? docStages : videoStages;
    let stageIndex  = 0;
    let stageStart  = Date.now();

    setProgressValue(2);
    setProgressStageIdx(stages[0].stageIdx);
    lastRealPctRef.current = 0;
    simStartRef.current    = Date.now();

    // ── Stufenbasierte Simulation ──────────────────────────────────────────
    // Interpoliert linear zwischen Stage-Ankern. Reale Backend-Werte gewinnen
    // via Math.max — sie bleiben immer Untergrenze.
    simIntervalRef.current = setInterval(() => {
      if (stageIndex >= stages.length - 1) return; // letzte Stage erreicht

      const current = stages[stageIndex];
      const next    = stages[stageIndex + 1];
      const elapsed = Date.now() - stageStart;
      const frac    = Math.min(elapsed / current.duration, 1);

      // Lineare Interpolation zwischen aktuellem und nächstem Anker
      const simPct  = Math.round(current.pct + frac * (next.pct - current.pct));
      const display = Math.max(simPct, lastRealPctRef.current, 2);

      setProgressValue(Math.min(display, 92));
      setProgressStageIdx(current.stageIdx);

      // Nächste Stage betreten wenn Zeit abgelaufen
      if (frac >= 1) {
        stageIndex++;
        stageStart = Date.now();
        if (stageIndex < stages.length) {
          setProgressStageIdx(stages[stageIndex].stageIdx);
        }
      }
    }, 300);

    // ── Echter Backend-Poll (best-effort, gewinnt immer) ──────────────────
    const baseUrl = appConfig.apiBaseUrl.replace(/\/$/, '');

    progressPollRef.current = setInterval(async () => {
      try {
        const authToken = token || localStorage.getItem('hejvi_access_token') || localStorage.getItem('token');
        const res  = await fetch(`${baseUrl}/element-progress?token=${progressToken}`, {
          headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 404) {
            clearInterval(progressPollRef.current);
            progressPollRef.current = null;
          }
          return;
        }
        const data = await res.json();

        if (data.pct > 0) {
          const cappedPct = Math.min(data.pct, 92);
          // Realen Wert als neue Untergrenze setzen
          if (cappedPct > lastRealPctRef.current) {
            lastRealPctRef.current = cappedPct;
            setProgressValue(cappedPct);
          }

          // Stage-Index aus Backend-Stage-Name ableiten
          const stageMap = isDoc ? {
            start: 0, moderation: 0,
            text_extract: 1, doc_meta: 1, text_done: 1,
            img_extract: 2, img_extracted: 2, img_filtered: 2,
            summary: 3, summary_done: 3, thumbnail: 3, translations: 3,
            audio_de: 4, audio_en: 4, audio_fr: 4, audio_es: 4,
            audio_ru: 4, audio_pl: 4, audio_ar: 4, audio_ro: 4, audio_tr: 4, audio_hr: 4,
            db_save: 5, vectorize: 5,
            ffmpeg: 6, done: 6,
          } : {
            start: 0, moderation: 0,
            script_gen: 1, transcribing: 1,
            translations: 2,
            audio_de: 3, audio_en: 3, audio_fr: 3, audio_es: 3,
            audio_ru: 3, audio_pl: 3, audio_ar: 3, audio_ro: 3, audio_tr: 3, audio_hr: 3,
            db_save: 4, thumbnail: 4, vectorize: 4,
            ffmpeg: 5, done: 5,
          };
          const backendIdx = stageMap[data.stage];
          if (backendIdx !== undefined) {
            // Backend-Stage vorwärts scrubben (nie zurückspringen)
            if (backendIdx > stageIndex) {
              stageIndex = backendIdx;
              stageStart = Date.now();
            }
            setProgressStageIdx(backendIdx);
          }
        }

        if (data.done) {
          clearInterval(progressPollRef.current);
          progressPollRef.current = null;
        }
      } catch (_) { /* Netzwerkfehler ignorieren – Simulation läuft weiter */ }
    }, 800);

    return stopAll;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  // Wenn Upload-Modus wechselt, Dateien der anderen Seite zurücksetzen
  const handleUploadModeChange = (newMode) => {
    setUploadMode(newMode);
    setError('');
    if (newMode === 'media') {
      // Dokument-Felder leeren
      setFormData(prev => ({
        ...prev,
        documentFile: null,
        documentFileName: null,
        documentMimeType: null,
        isDocumentMode: false,
      }));
    } else {
      // Medien-Felder leeren
      if (formData.mediaPreviewUrl) {
        URL.revokeObjectURL(formData.mediaPreviewUrl);
      }
      setFormData(prev => ({
        ...prev,
        mediaFile: null,
        mediaType: null,
        mediaPreviewUrl: null,
        isDocumentMode: true,
      }));
    }
  };

  // --- HANDLERS ---

  const handleNext = () => {
    if (!validateStep(step)) {
        return; 
    }
    console.log ("handleNext called now. step: " + step);

    // 2. ZWISCHENSCHRITT LOGIK (Nach Step 3)
    if (step === 3) {
         // Skript-Review nur wenn kein Dokument-Modus und Feature aktiv
         console.log ("Step 3 is active : State: ENABLE_SCRIPT_REVIEW: " + ENABLE_SCRIPT_REVIEW + " selected step: " + selectedScript)
         if (ENABLE_SCRIPT_REVIEW && !formData.useExactText && !formData.useVideoTranscription && !selectedScript && !formData.isDocumentMode) {
             setShowScriptSelection(true);
             console.log ("Suggestions active.");
             
             // Automatisch laden, wenn noch leer
             if (scriptOptions.length === 0) {
                  console.log ("Getting suggestions...");
                 fetchScriptSuggestions();
             }
             return; // WICHTIG: Hier stoppen wir, damit NICHT zu Step 4 gewechselt wird!
         }
    }

    // 3. Standard-Verhalten: Weiter zum nächsten Schritt
    setError('');
    setStepError('');
    setStep(prev => prev + 1);

  };

  const handleBack = () => {
      // Wenn wir tiefer im Prozess sind (Schritt 2, 3...), gehen wir einen Schritt zurück
      if (step > 1) {
        setStepError('');
        setStep(step - 1);
      } else {
        // Wenn wir im ersten Schritt sind (oder abbrechen wollen):
        // Prüfen, ob wir eine Rücksprung-Adresse haben (z.B. vom CourseBuilder)
        if (location.state && location.state.returnUrl) {
            navigate(location.state.returnUrl);
        } else {
            navigate('/profile'); // Fallback, falls kein Ziel angegeben wurde
        }
      }
    };

  const validateStep = (currentStep) => {
    switch(currentStep) {
      case 1: // Name
        if (!formData.name.trim()) {
          setError(t('pleaseEnterName') || 'Please enter a name for your element.');
          return false;
        }
        return true;
      case 2: // Media ODER Dokument
        if (uploadMode === 'document') {
          if (!formData.documentFile) {
            setError(t('pleaseSelectDocument') || 'Bitte wähle ein Dokument aus (PDF, TXT oder DOC).');
            return false;
          }
        } else {
          if (!formData.mediaFile) {
            setError(t('pleaseSelectMedia') || 'Please select a video or photo.');
            return false;
          }
        }
        return true;
      case 3: // Description
        // Im Dokumenten-Modus oder bei Video-Transkription ist die Beschreibung optional
        if (!formData.isDocumentMode && !formData.useVideoTranscription) {
          if (!formData.description.trim() || formData.description.length < 10) {
            setStepError(t('descTooShort') || 'Bitte gib eine Beschreibung ein (min. 10 Zeichen).');
            return false;
          }
        }
        setStepError('');
        return true;
      default:
        return true;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset Transkriptions-Toggle wenn neue Datei gewählt
    setFormData(prev => ({ ...prev, useVideoTranscription: false, useExactText: false }));

    // Basic Validation
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      setError(t('invalidFileType') || 'Please upload a valid video or image.');
      return;
    }

    // Size limit check (e.g. 100MB for video/image)
    const maxSize = (window.HEJVI_SETTINGS?.maxMediaUploadMB ?? 100) * 1024 * 1024; 
    if (file.size > maxSize) {
      setError(t('fileTooLarge') || `File is too large. Maximum size is ${window.HEJVI_SETTINGS?.maxMediaUploadMB ?? 100}MB.`);
      return;
    }

    // Create Preview
    const previewUrl = URL.createObjectURL(file);
    
    setFormData(prev => ({
      ...prev,
      mediaFile: file,
      mediaType: isVideo ? 'video' : 'image',
      mediaPreviewUrl: previewUrl,
      // Sicherstellen, dass Dokument-Modus deaktiviert
      isDocumentMode: false,
      documentFile: null,
      documentFileName: null,
      documentMimeType: null,
    }));
    setError('');
  };

  // NEU: Handler für Dokument-Upload
  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Typ-Validierung
    if (!DOCUMENT_MIME_TYPES.includes(file.type)) {
      setError(t('invalidDocumentType') || 'Bitte lade eine gültige Datei hoch: PDF, TXT, DOC/DOCX oder PPT/PPTX.');
      return;
    }

    // Größenprüfung: Limit zur Laufzeit lesen, damit settings.js bereits geladen ist
    const DOCUMENT_MAX_SIZE = (window.HEJVI_SETTINGS?.maxDocumentUploadMB ?? 10) * 1024 * 1024;
    if (file.size > DOCUMENT_MAX_SIZE) {
      setError(t('documentTooLarge') || `Die Datei ist zu groß. Maximal ${DOCUMENT_MAX_SIZE / 1024 / 1024} MB erlaubt.`);
      return;
    }

    setFormData(prev => ({
      ...prev,
      documentFile: file,
      documentFileName: file.name,
      documentMimeType: file.type,
      isDocumentMode: true,
      // Medien-Felder leeren
      mediaFile: null,
      mediaType: null,
      mediaPreviewUrl: null,
    }));
    setError('');
  };

  // NEU: Dokument entfernen
  const handleRemoveDocument = () => {
    setFormData(prev => ({
      ...prev,
      documentFile: null,
      documentFileName: null,
      documentMimeType: null,
      isDocumentMode: false,
    }));
    // Input zurücksetzen
    if (documentFileInputRef.current) {
      documentFileInputRef.current.value = '';
    }
    setError('');
  };

  // NEU: Medium entfernen
  const handleRemoveMedia = () => {
    if (formData.mediaPreviewUrl) {
      URL.revokeObjectURL(formData.mediaPreviewUrl);
    }
    setFormData(prev => ({
      ...prev,
      mediaFile: null,
      mediaType: null,
      mediaPreviewUrl: null,
      useExactText: false,
      useVideoTranscription: false,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setError('');
  };

  // --- SCRIPT SELECTION HANDLERS ---

  const handleScriptSelect = (text) => {
    setSelectedScript(stripScriptPrefix(text));
    setShowScriptSelection(false);
    // Jetzt explizit zu Schritt 4 (Review) gehen
    setStep(4); 
  };

  const handleScriptRetry = () => {
    if (scriptRetryCount >= 3) {
        alert(t('maxRetriesReached') || "Bitte präzisiere deine Beschreibung. Keine weiteren Versuche möglich.");
        // Zurück zum Editor (Step 3), um Beschreibung zu ändern
        setShowScriptSelection(false); 
        setScriptRetryCount(0);
        setScriptOptions([]);
        return;
    }

    setScriptRetryCount(prev => prev + 1);
    fetchScriptSuggestions(); // Neue Variante anfordern
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    setError('');

    try {
      const data = new FormData();

      // --- SECURITY: INPUTS BEREINIGEN ---
      const cleanName = sanitizeInput(formData.name);
      const cleanDesc = sanitizeInput(formData.description);
      
      if (!cleanName) {
          throw new Error(t('invalidInput') || "Die Eingabe enthält ungültige Zeichen.");
      }

      const baseUrl = appConfig.apiBaseUrl.replace(/\/$/, '');
      let authToken = token;
      if (!authToken) {
         authToken = localStorage.getItem('token') || localStorage.getItem('hejvi_access_token');
      }

      // -----------------------------------------------------------------------
      // Video-Transkription – läuft während des Processing-Screens
      // -----------------------------------------------------------------------
      if (formData.useVideoTranscription && formData.mediaType === 'video' && formData.mediaFile) {
        const transcribeForm = new FormData();
        transcribeForm.append('video', formData.mediaFile);
        transcribeForm.append('auth_token', authToken);

        const transcribeResp = await fetch(`${baseUrl}/transcribe-video`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` },
          body: transcribeForm,
        });
        const transcribeResult = await transcribeResp.json();

        if (!transcribeResp.ok || transcribeResult.status !== 'success') {
          throw new Error(transcribeResult.error || `Transkription fehlgeschlagen (${transcribeResp.status})`);
        }

        const transcript   = transcribeResult.transcript || '';
        const detectedLang = transcribeResult.language   || '';
        const segments     = transcribeResult.segments   || [];

        // Transkript immer als Beschreibung senden
        data.append('detected_language', detectedLang);

        if (formData.useExactText) {
          // 1:1-Modus: Transkript 1:1 + echte Whisper-Zeitstempel für synchrone Untertitel
          data.append('description',         transcript);
          data.append('video_transcript',    transcript);
          data.append('transcript_segments', JSON.stringify(segments));
          data.append('use_exact_text', '1');
        } else {
          // LLM-Modus: Transkript als Beschreibungsbasis, KI generiert daraus ein Skript
          // Keine Whisper-Segmente – TTS-Audio hat eigene Timings
          data.append('description',         transcript);
          data.append('video_transcript',    '');
          data.append('transcript_segments', '[]');
          data.append('use_exact_text', '0');
        }
      }
      // -----------------------------------------------------------------------

      data.append('name', formData.name);
      // Beschreibungsfeld nur anhängen wenn NICHT useVideoTranscription
      // (oben bereits korrekt gesetzt)
      if (!formData.useVideoTranscription) {
        data.append('description', formData.description || '');
      }
      // --- ÄNDERUNG: Skript nur senden, wenn Exact Text AUS ist ---
      if (selectedScript && !formData.useExactText && !formData.isDocumentMode) {
          data.append('selected_script', selectedScript);
      }
      // ------------------------------------------------------------
      if (user && user.id) {
        data.append('userid', user.id);
      }
      // use_exact_text nur anhängen wenn nicht bereits von Transkriptions-Block gesetzt
      if (!formData.useVideoTranscription) {
        data.append('use_exact_text', formData.useExactText ? '1' : '0');
      }

      // Aktuelle Sprache mitsenden (Fallback auf 'de')
      // Bei Transkription überschreibt detected_language die source_language im VideoBuilder
      data.append('source_language', currentLanguage || 'de');
      
      // NEU: Dokument-Modus Flag
      data.append('document_mode', formData.isDocumentMode ? '1' : '0');

      // Datei immer als 'media_file' senden – der bestehende Controller-Validator
      // erwartet dieses Feld. Das Flag document_mode=1 signalisiert VideoBuilder
      // intern den Dokumenten-Verarbeitungspfad.
      if (formData.isDocumentMode && formData.documentFile) {
        data.append('media_file', formData.documentFile);
      } else if (formData.mediaFile) {
        data.append('media_file', formData.mediaFile);
      }

      // --- NEU: CONTENT TYPE LOGIK (NUR ADMINS) ---
      // Wenn Reaktionsvideo aktiv -> Typ 4, sonst Standard (1)
      const contentType = formData.isReactionVideo ? '4' : '1';
      data.append('content_type', contentType);
      // --------------------------------------------
      
      data.append('auth_token', authToken);
      data.append('progress_token', progressToken);
      // Für Admins: assign_to_general Flag mitsenden (Normal-User: immer 1)
      data.append('assign_to_general', (!isAdminUser || assignToGeneral) ? '1' : '0');

      console.log("Transferring Data to api: " + currentLanguage || 'de');
      console.log("Name: " + formData.name);
      console.log("Document mode: " + formData.isDocumentMode);
      
      const response = await fetch(`${baseUrl}/create-element`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: data
      });

      // --- Robustes JSON-Parsing: Antwort könnte bei Timeout HTML sein ---
      let result;
      const rawText = await response.text();
      try {
        result = JSON.parse(rawText);
      } catch (_) {
        // Server hat kein JSON zurückgegeben (z.B. 504 HTML-Seite)
        if (response.status === 504 || response.status === 502 || response.status === 0) {
          const timeoutErr = new Error('TIMEOUT');
          timeoutErr.isTimeout = true;
          throw timeoutErr;
        }
        throw new Error(`Server-Fehler (${response.status})`);
      }

      // --- ERROR HANDLING MIT ÜBERSETZUNG ---
      if (!response.ok || result.status === 'error') {
          let errorMsg = result.message || `Upload fehlgeschlagen (${response.status})`;

          if (response.status === 504 || response.status === 502) {
            const timeoutErr = new Error('TIMEOUT');
            timeoutErr.isTimeout = true;
            throw timeoutErr;
          }

          // Check auf Moderations-Fehler
          if (errorMsg.includes('Content rejected') || errorMsg.includes('moderation')) {
              errorMsg = t('errorContentRejected') || 'Dein Inhalt wurde abgelehnt, da er gegen die Sicherheitsrichtlinien verstößt (z.B. Beleidigung, Gewalt).';
          }
          
          throw new Error(errorMsg);
      }

      if (result.status === 'success') {
        const isPendingApproval = result.pending_approval === true;

        // --- Workflow 1: Rückkehr zu einem bestehenden Kurs (von CourseDetail) ---
        // Bei pending_approval NICHT automatisch weiterleiten — User soll Pending-Screen sehen
        const returnToCourseHash = sessionStorage.getItem('hejvi_return_to_course');
        if (returnToCourseHash && !isPendingApproval) {
            sessionStorage.removeItem('hejvi_return_to_course');
            const newElementId = result.videoId || result.id;

            const newElementForCourse = {
                id: newElementId,
                hash_id: result.hash_id || null,
                name: formData.name,
                description: formData.description,
                url_thumbnail: result.url_thumbnail || formData.mediaPreviewUrl || null,
                type: 1,
                content_type: 1,
                progress: 0,
                duration: result.duration || 0
            };
            sessionStorage.setItem(
                `hejvi_new_element_for_course_${returnToCourseHash}`,
                JSON.stringify(newElementForCourse)
            );

            const tryAddToCollection = async () => {
                try {
                    await addElementToCollection(returnToCourseHash, newElementId);
                    sessionStorage.setItem('hejvi_course_updated', returnToCourseHash);
                } catch (err) {
                    console.warn('add-element request failed:', err);
                }
            };

            tryAddToCollection();

            navigate(`/course/${returnToCourseHash}`, {
                state: { newElementAdded: true, newElementId }
            });
            return;
        }

        // --- Workflow 2: Rückkehr zum CourseBuilder (Draft vorhanden) ---
        // Bei pending_approval ebenfalls Pending-Screen zeigen
        const draft = sessionStorage.getItem('hejvi_course_draft');
        if (draft && !isPendingApproval) {
            const newElementForBuilder = {
                id: result.videoId || result.id,
                name: formData.name,
                description: formData.description,
                url_thumbnail: result.url_thumbnail || formData.mediaPreviewUrl,
                type: 1,
                content_type: 1
            };
            
            sessionStorage.setItem('hejvi_created_element', JSON.stringify(newElementForBuilder));
            navigate('/create-course');
            return;
        }
        // ---------------------------

        setCreatedElementData(result);
        setIsProcessing(false);
        setStep(6); 
      }

    } catch (err) {
      console.error("Upload failed", err);
      setIsProcessing(false);
      if (err.isTimeout || err.message === 'TIMEOUT') {
        setUploadErrorType('timeout');
        setError('timeout');
      } else {
        setUploadErrorType('general');
        setError(err.message || (t('uploadFailed') || 'Upload fehlgeschlagen. Bitte erneut versuchen.'));
      }
    }
  };

  // --- NIIMBOT DRUCKER FUNKTIONEN ---

  const loadImageToCanvas = useCallback((imageUrl) => {
    return new Promise((resolve, reject) => {
      const LABEL_W = 400;
      const LABEL_H = 240;
      const canvas = printCanvasRef.current;
      canvas.width  = LABEL_W;
      canvas.height = LABEL_H;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, LABEL_W, LABEL_H);

      const drawQR = (img) => {
        const padding = 10;
        const size = LABEL_H - padding * 2;
        const x = Math.round((LABEL_W - size) / 2);
        const y = padding;
        ctx.drawImage(img, x, y, size, size);
        resolve(canvas);
      };

      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => drawQR(img);

      img.onerror = () => {
        fetch(imageUrl)
          .then(res => res.blob())
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const img2 = new Image();
            img2.onload = () => { drawQR(img2); URL.revokeObjectURL(blobUrl); };
            img2.onerror = () => reject(new Error('Bild konnte nicht geladen werden.'));
            img2.src = blobUrl;
          })
          .catch(reject);
      };

      img.src = imageUrl;
    });
  }, []);

  const connectPrinter = async () => {
    setPrintStatus('connecting');
    try {
      const { NiimbotBluetoothClient } = await import('@mmote/niimbluelib');
      const client = new NiimbotBluetoothClient();
      await client.connect();
      setPrinterClient(client);
      setPrinterConnected(true);
      setPrintStatus('idle');
    } catch (err) {
      console.error('[Niimbot] Verbindung fehlgeschlagen:', err);
      setPrintStatus('error');
    }
  };

  const printQRLabel = async () => {
    if (!createdElementData?.qr_direct_url) return;

    let lib;
    try {
      lib = await import('@mmote/niimbluelib');
    } catch (err) {
      console.error('[Niimbot] Library konnte nicht geladen werden:', err);
      setPrintStatus('error');
      return;
    }

    const { NiimbotBluetoothClient, ImageEncoder, Abstraction, B1PrintTask } = lib;

    let client = printerClient;
    if (!printerConnected || !client) {
      setPrintStatus('connecting');
      try {
        client = new NiimbotBluetoothClient();

        client.on('disconnect', () => {
          setPrinterConnected(false);
          setPrinterClient(null);
          setPrintStatus('idle');
        });

        await client.connect();
        setPrinterClient(client);
        setPrinterConnected(true);
        console.log('[Niimbot] Verbunden');
      } catch (err) {
        console.error('[Niimbot] Verbindung fehlgeschlagen:', err);
        setPrintStatus('error');
        return;
      }
    }

    setPrintStatus('printing');
    try {
      const canvas = await loadImageToCanvas(createdElementData.qr_direct_url);

      const labelProps = {
        width:          canvas.width,
        height:         canvas.height,
        printDirection: 'left',
      };

      const encoded = ImageEncoder.encodeCanvas(canvas, labelProps);

      const abstraction = new Abstraction(client);
      const task = new B1PrintTask(abstraction);
      await task.printInit(encoded, 1);
      await task.printPage(encoded, 1);
      await task.waitForFinished();

      setPrintStatus('done');
      console.log('[Niimbot] Druck erfolgreich!');
    } catch (err) {
      console.error('[Niimbot] Druckfehler:', err);
      setPrintStatus('error');
    }
  };

  // --- RENDER STEPS ---

  const nameInputRef = useRef(null);

  // Fokus auf das Namensfeld setzen wenn Schritt 1 aktiv ist
  useEffect(() => {
    if (step === 1 && nameInputRef.current) {
      // Kleines Timeout damit das DOM vollständig gerendert ist
      setTimeout(() => nameInputRef.current?.focus(), 50);
    }
  }, [step]);

  const renderStep1_Name = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">{t('createStep1Title') || "Let's start! Give it a name."}</h2>
      <input 
        ref={nameInputRef}
        type="text" 
        className="ec-input"
        placeholder={t('elementNamePlaceholder') || "e.g., How to fix the milling machine"}
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        onKeyDown={(e) => { if (e.key === 'Enter') handleNext(); }}
        autoFocus
      />
    </div>
  );

  const renderStep2_Media = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">{t('createStep2Title') || "Upload your content"}</h2>
      <p className="ec-subtitle">{t('createStep2Sub') || "Take a photo/video, upload from library, or add a document."}</p>
      
      {/* --- NEU: TAB SWITCHER --- */}
      <div className="ec-upload-tabs">
        <button
          className={`ec-upload-tab ${uploadMode === 'media' ? 'active' : ''}`}
          onClick={() => handleUploadModeChange('media')}
          type="button"
        >
          <span className="ec-upload-tab-icon">🎬</span>
          <span>{t('photoVideo') || 'Foto / Video'}</span>
        </button>
        <button
          className={`ec-upload-tab ${uploadMode === 'document' ? 'active' : ''}`}
          onClick={() => handleUploadModeChange('document')}
          type="button"
        >
          <span className="ec-upload-tab-icon">📄</span>
          <span>{t('document') || 'Dokument'}</span>
        </button>
      </div>
      {/* ----------------------- */}

      {uploadMode === 'media' ? (
        /* --- MEDIEN UPLOAD (Photo/Video) --- */
        <>
          <div 
            className="ec-upload-area"
            onClick={() => fileInputRef.current.click()}
          >
            {formData.mediaPreviewUrl ? (
              <div className="ec-preview-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', backgroundColor: '#000', overflow: 'hidden' }}>
                {formData.mediaType === 'video' ? (
                  <video 
                    src={formData.mediaPreviewUrl} 
                    className="ec-media-preview" 
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    controls={false} 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                  />
                ) : (
                  <img 
                    src={formData.mediaPreviewUrl} 
                    alt="Preview" 
                    className="ec-media-preview" 
                    style={{ 
                        width: 'auto', 
                        height: 'auto', 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain',
                        display: 'block' 
                    }}
                  />
                )}
                <div className="ec-change-media-overlay">
                  <span>{t('tapToChange') || 'Tap to change'}</span>
                </div>
              </div>
            ) : (
              <div className="ec-upload-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>{t('selectMedia') || 'Select Media'}</span>
              </div>
            )}
          </div>

          {/* NEU: Entfernen-Button wenn Datei ausgewählt */}
          {formData.mediaPreviewUrl && (
            <button
              className="ec-btn-remove"
              onClick={(e) => { e.stopPropagation(); handleRemoveMedia(); }}
              type="button"
            >
              ✕ {t('removeFile') || 'Datei entfernen'}
            </button>
          )}

          <input 
            type="file" 
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="video/*,image/*"
            style={{ display: 'none' }}
          />
        </>
      ) : (
        /* --- NEU: DOKUMENT UPLOAD --- */
        <>
          {formData.documentFile ? (
            /* Dokument wurde hochgeladen – Info-Box anzeigen */
            <div className="ec-document-preview">
              <div className="ec-document-icon">
                {getDocumentIcon(formData.documentMimeType)}
              </div>
              <div className="ec-document-info">
                <span className="ec-document-badge">
                  {getDocumentTypeName(formData.documentMimeType)}
                </span>
                <span className="ec-document-name" title={formData.documentFileName}>
                  {formData.documentFileName}
                </span>
                <span className="ec-document-size">
                  {(formData.documentFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <div className="ec-document-actions">
                <button
                  className="ec-btn-doc-replace"
                  onClick={() => documentFileInputRef.current.click()}
                  type="button"
                >
                  🔄 {t('replaceFile') || 'Ersetzen'}
                </button>
                <button
                  className="ec-btn-doc-remove"
                  onClick={handleRemoveDocument}
                  type="button"
                >
                  ✕ {t('removeFile') || 'Entfernen'}
                </button>
              </div>
            </div>
          ) : (
            /* Kein Dokument – Upload-Bereich zeigen */
            <div 
              className="ec-upload-area ec-document-upload-area"
              onClick={() => documentFileInputRef.current.click()}
            >
              <div className="ec-upload-placeholder">
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>📄</div>
                <span style={{ fontWeight: 600 }}>{t('selectDocument') || 'Dokument auswählen'}</span>
                <span style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '4px' }}>
                  PDF, TXT, DOC oder DOCX · max. {window.HEJVI_SETTINGS?.maxDocumentUploadMB ?? 10} MB
                </span>
              </div>
            </div>
          )}

          {/* Info-Box: Was passiert mit dem Dokument? */}
          <div className="ec-info-box" style={{ marginTop: '16px' }}>
            <span className="ec-info-icon">🤖</span>
            <p>
              {t('documentAiInfo') || 
                'Die KI extrahiert automatisch den Inhalt des Dokuments, erstellt eine kurze Zusammenfassung (1–2 Sätze) als Videotext und nutzt die erste Seite als Vorschaubild.'}
            </p>
          </div>

          <input 
            type="file" 
            ref={documentFileInputRef}
            onChange={handleDocumentChange}
            accept=".pdf,.txt,.doc,.docx,.ppt,.pptx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );

  const renderStep3_Description = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">{t('createStep3Title') || "Explain it"}</h2>

      {/* NEU: Dokument-Modus Info-Box */}
      {formData.isDocumentMode ? (
        <>
          <div className="ec-info-box" style={{ marginBottom: '16px' }}>
            <span className="ec-info-icon">✅</span>
            <div>
              <p style={{ marginBottom: '4px', fontWeight: 600 }}>
                {t('documentModeActive') || 'Dokument-Modus aktiv'}
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {t('documentModeDesc') || 
                  'Die KI wird den Inhalt deines Dokuments automatisch als Videotext verwenden. Du kannst optional zusätzlichen Kontext hinzufügen oder das Feld leer lassen.'}
              </p>
            </div>
          </div>
          <div className="ec-document-uploaded-badge" style={{ marginBottom: '12px' }}>
            <span>{getDocumentIcon(formData.documentMimeType)}</span>
            <span style={{ marginLeft: '8px', fontSize: '14px', color: '#6B7280' }}>
              {formData.documentFileName}
            </span>
          </div>
          <textarea 
            className="ec-textarea"
            placeholder={t('docAdditionalContextPlaceholder') || "Optionaler zusätzlicher Kontext (z.B. Zielgruppe, Fokusbereich)..."}
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={4}
          />
        </>
      ) : formData.useVideoTranscription ? (
        /* Video-Transkription aktiv → Beschreibung wird automatisch befüllt */
        <div className="ec-info-box" style={{ marginBottom: '16px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
          <span className="ec-info-icon">🎙️</span>
          <div>
            <p style={{ marginBottom: '4px', fontWeight: 600, color: '#0369a1' }}>
              {t('transcribeVideoTitle') || 'Videoton als Voiceover verwenden'}
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
              {formData.useExactText
                ? (t('transcribeExactInfo') || 'Der gesprochene Text wird 1:1 transkribiert und als Voiceover übernommen. Die Untertitel bleiben synchron zum Originalbild.')
                : (t('transcribeLLMInfo') || 'Der gesprochene Text wird transkribiert. Die KI erstellt daraus ein optimiertes Voiceover-Skript in der erkannten Videosprache.')
              }
            </p>
          </div>
        </div>
      ) : (
        <>
          <p className="ec-subtitle">{t('createStep3Sub') || "What is happening in this video? Our AI will turn this into a professional narration."}</p>
          <textarea 
            className={`ec-textarea${stepError ? ' ec-textarea-error' : ''}`}
            placeholder={t('descPlaceholder') || "Describe the process step by step..."}
            value={formData.description}
            onChange={(e) => {
              setFormData({...formData, description: e.target.value});
              if (e.target.value.trim().length >= 10) setStepError('');
            }}
            rows={6}
          />
          {stepError && (
            <div style={{
              marginTop: '8px',
              padding: '10px 14px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '10px',
              color: '#DC2626',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span>⚠️</span>
              <span>{stepError}</span>
            </div>
          )}
        </>
      )}

      {/* --- TOGGLE SWITCHES (NUR FÜR GRUPPE 0) --- */}
      {hasFeature('super_admin') && !formData.isDocumentMode && (
        <>
            {/* Schalter: Exact Text – bei aktivem Transkriptions-Modus andere Beschriftung */}
            {!formData.useVideoTranscription && (
              <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s'}}>
                  <label className="ec-switch">
                  <input 
                      type="checkbox" 
                      checked={formData.useExactText}
                      onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData(prev => ({...prev, useExactText: checked}));
                              if (checked) {
                                  setSelectedScript(null);
                                  setScriptOptions([]);
                              }
                          }}
                  />
                  <span className="ec-slider"></span>
                  </label>
                  <div className="ec-toggle-label">
                      <strong>{t('useExactTextTitle') || "Text exakt vertonen"}</strong>
                      <span>
                          {t('useExactTextDesc') || "KI-Optimierung überspringen und Beschreibung 1:1 vorlesen."}
                      </span>
                  </div>
              </div>
            )}

            {/* Bei Transkription: "Exact Text" bedeutet 1:1-Transkript statt KI-Skript */}
            {formData.useVideoTranscription && (
              <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s'}}>
                  <label className="ec-switch">
                  <input 
                      type="checkbox" 
                      checked={formData.useExactText}
                      onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData(prev => ({...prev, useExactText: checked}));
                          }}
                  />
                  <span className="ec-slider"></span>
                  </label>
                  <div className="ec-toggle-label">
                      <strong>{t('useExactTextTitle') || "Text exakt vertonen"}</strong>
                      <span style={{color: '#0369a1'}}>
                          {t('transcribeExactToggleDesc') || 'Transkription 1:1 übernehmen (synchron zum Bild). Aus: KI erstellt optimiertes Skript aus dem Transkript.'}
                      </span>
                  </div>
              </div>
            )}

            {/* REAKTIONSVIDEO SCHALTER */}
            <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s', marginTop: '15px'}}>
                <label className="ec-switch">
                <input 
                    type="checkbox" 
                    checked={formData.isReactionVideo}
                    onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData(prev => ({...prev, isReactionVideo: checked}));
                    }}
                />
                <span className="ec-slider" style={{backgroundColor: formData.isReactionVideo ? '#8B5CF6' : ''}}></span>
                </label>
                
                <div className="ec-toggle-label">
                    <strong style={{color: formData.isReactionVideo ? '#8B5CF6' : ''}}>{t('reactionVideo') || 'Reaktionsvideo'}</strong>
                    <span>
                        {t('setThisToReaction') || 'Markiert dieses Element als Antwort-Video (Content Type 4)'}.
                    </span>
                </div>
            </div>

            {/* ALLGEMEIN-ZUWEISUNG: nur für Admins (group 0/3) */}
            {isAdminUser && (
              <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s', marginTop: '15px'}}>
                <label className="ec-switch">
                  <input
                    type="checkbox"
                    checked={assignToGeneral}
                    onChange={(e) => setAssignToGeneral(e.target.checked)}
                  />
                  <span className="ec-slider" style={{backgroundColor: assignToGeneral ? '#10b981' : '#ef4444'}}></span>
                </label>
                <div className="ec-toggle-label">
                  <strong style={{color: assignToGeneral ? '#10b981' : '#ef4444'}}>
                    {t('assignToGeneral') || 'Zur Allgemein-Gruppe hinzufügen'}
                  </strong>
                  <span>
                    {assignToGeneral
                      ? (t('assignToGeneralOn') || 'Inhalt wird nach Erstellung für alle Nutzer der Standard-Gruppe sichtbar.')
                      : (t('assignToGeneralOff') || '⚠ Achtung: Dieser Inhalt wird nicht für die Allgemeinheit sichtbar sein. Er ist nur für Admins und den Ersteller zugänglich.')}
                  </span>
                </div>
              </div>
            )}

            {/* VIDEO-TRANSKRIPTION SCHALTER (nur wenn Videodatei hochgeladen) */}
            {formData.mediaType === 'video' && (
              <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s', marginTop: '15px'}}>
                  <label className="ec-switch">
                  <input 
                      type="checkbox" 
                      checked={formData.useVideoTranscription}
                      onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                              ...prev,
                              useVideoTranscription: checked,
                              // Kombinierbar: useExactText wird durch Transkription impliziert;
                              // wenn Transkription an, anderen exact-text-Schalter ausschalten
                              useExactText: checked ? false : prev.useExactText,
                          }));
                          if (checked) {
                              setSelectedScript(null);
                              setScriptOptions([]);
                          }
                      }}
                  />
                  <span className="ec-slider" style={{backgroundColor: formData.useVideoTranscription ? '#0ea5e9' : ''}}></span>
                  </label>
                  
                  <div className="ec-toggle-label">
                      <strong style={{color: formData.useVideoTranscription ? '#0ea5e9' : ''}}>
                          {t('transcribeVideoTitle') || 'Videoton als Voiceover verwenden'}
                      </strong>
                      <span>
                          {t('transcribeVideoDesc') || 'Der gesprochene Text wird transkribiert, die erkannte Sprache als Quellsprache gesetzt und Untertitel synchron zum Bild generiert.'}
                      </span>
                  </div>
              </div>
            )}
        </>
      )}
      {/* ------------------------------------------- */}
    </div>
  );

  const renderScriptSelection = () => (
    <div className="ec-step-content fade-in">
        <h2 className="ec-title">{t('selectModeration') || 'Wähle den passenden Text aus'}</h2>
        <p className="ec-subtitle">
            {t('selectModerationDesc') || 'Die KI hat folgende Textvorschläge basierend auf deiner Beschreibung erstellt:'}
        </p>

        {isLoadingScripts ? (
            <div className="ec-loading-container" style={{textAlign: 'center', padding: '40px'}}>
                <div className="ec-spinner" style={{margin: '0 auto 20px'}}></div>
                <p>{t('generatingScripts') || 'Texte werden generiert...'}</p>
            </div>
        ) : (
            <div className="ec-script-options">
                {scriptOptions.map((text, idx) => {
                    const isGroup0 = user && parseInt(user.group) === 0;
                    const isOriginal = isGroup0 && idx === scriptOptions.length - 1;
                    
                    let badgeLabel = `Option ${idx + 1}`;
                    if (isGroup0) {
                        if (isOriginal) {
                            badgeLabel = t('originalText') || 'Original';
                        } else {
                            badgeLabel = `AI Option ${idx + 1}`;
                        }
                    }

                    return (
                        <div 
                            key={idx} 
                            className="ec-script-card"
                            onClick={() => handleScriptSelect(text)}
                            style={{
                                border: isOriginal ? '1px solid #10B981' : undefined
                            }}
                        >
                            <div 
                                className="ec-script-badge"
                                style={{
                                    backgroundColor: isOriginal ? '#10B981' : undefined 
                                }}
                            >
                                {badgeLabel}
                            </div>
                            <p>{stripScriptPrefix(text)}</p>
                            <button 
                                className="ec-btn-select"
                                style={{
                                    backgroundColor: isOriginal ? '#10B981' : undefined
                                }}
                            >
                                {t('select') || 'Auswählen'}
                            </button>
                        </div>
                    );
                })}
            </div>
        )}

        {!isLoadingScripts && (
            <div className="ec-actions-center" style={{marginTop: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px'}}>
                <button 
                    className="ec-btn-text" 
                    onClick={handleScriptRetry}
                    style={{color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline'}}
                >
                    ↻ {t('nothingFits') || 'Nichts passt'} ({3 - scriptRetryCount} Versuche übrig)
                </button>
                
                <button 
                    className="ec-btn ec-btn-secondary" 
                    onClick={() => setShowScriptSelection(false)}
                >
                    {t('backToDesc') || 'Zurück zur Beschreibung'}
                </button>
            </div>
        )}
    </div>
  );

  const renderStep4_Review = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">{t('createStep4Title') || "Review"}</h2>
      
      <div className="ec-review-card">
        <div className="ec-review-row">
          <span className="ec-label">{t('name')}:</span>
          <span className="ec-value">{formData.name}</span>
        </div>

        {/* NEU: Dokument ODER Medien-Anzeige im Review */}
        {formData.isDocumentMode ? (
          <div className="ec-review-row">
            <span className="ec-label">{t('document') || 'Dokument'}:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>{getDocumentIcon(formData.documentMimeType)}</span>
              <div>
                <span className="ec-value">{formData.documentFileName}</span>
                <span style={{ display: 'block', fontSize: '12px', color: '#9CA3AF' }}>
                  {getDocumentTypeName(formData.documentMimeType)} · {(formData.documentFile?.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="ec-review-row">
            <span className="ec-label">{t('media')}:</span>
            <span className="ec-value">
              {formData.mediaFile ? `${formData.mediaType.toUpperCase()} - ${(formData.mediaFile.size / 1024 / 1024).toFixed(1)} MB` : 'None'}
            </span>
          </div>
        )}

        {/* Beschreibung – im Dokument-Modus als optional markieren */}
        <div className="ec-review-row">
          <span className="ec-label">{t('description')}:</span>
          {formData.isDocumentMode && !formData.description ? (
            <span style={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>
              {t('autoGeneratedFromDoc') || 'Wird automatisch aus dem Dokument generiert'}
            </span>
          ) : (
            <p className="ec-value-block">{formData.description}</p>
          )}
        </div>

        {/* Modus-Anzeige */}
        {formData.isDocumentMode ? (
            <div className="ec-review-item" style={{marginTop: '10px'}}>
                <div className="ec-review-value" style={{
                    padding: '10px 15px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    color: '#15803d',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>🤖</span>
                    <span>{t('documentAutoProcess') || 'KI extrahiert Inhalt und erstellt Zusammenfassung als Videotext.'}</span>
                </div>
            </div>
        ) : formData.useExactText ? (
            <div className="ec-review-item" style={{marginTop: '10px'}}>
                <div className="ec-review-value" style={{
                    padding: '10px 15px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    color: '#1e40af',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>ℹ️</span>
                    <strong>{t('useExactTextTitle') || "Exakter Text Modus"}:</strong>
                    <span>{t('useExactTextDesc') || "Die Beschreibung wird 1:1 übernommen."}</span>
                </div>
            </div>
        ) : selectedScript && (
            <div className="ec-review-item">
                <label>{t('selectedModeration') || 'Gewählte Moderation'}:</label>
                <div className="ec-review-value" style={{
                    marginTop: '8px',
                    padding: '15px',
                    backgroundColor: '#1f2937',
                    borderRadius: '8px',
                    borderLeft: '4px solid #8b5cf6',
                    fontStyle: 'italic',
                    whiteSpace: 'pre-wrap',
                    color: '#e5e7eb',
                    fontSize: '0.95rem'
                }}>
                    "{selectedScript}"
                </div>
            </div>
        )}
      </div>

      <div className="ec-info-box">
        <span className="ec-info-icon">✨</span>
        <p>
          {formData.isDocumentMode
            ? (t('aiDocProcessingInfo') || 'Wir analysieren das Dokument, erzeugen eine Zusammenfassung als Voiceover und erstellen ein Video mit der ersten Seite als Vorschaubild.')
            : (t('aiProcessingInfo') || 'We will now compress the video, generate a professional voiceover from your description, and translate everything into 7 languages.')}
        </p>
      </div>
    </div>
  );

  const renderStep5_Processing = () => {
    const isDoc = formData.isDocumentMode;
    const hasTranscription = formData.useVideoTranscription;

    // Labels für die Stage-Punkte (visuell, nicht für die echte Steuerung)
    const stageLabels = isDoc
      ? [
          t('progressUploading')        || 'Hochladen',
          t('progressExtractingText')   || 'Text extrahieren',
          t('progressProcessingImages') || 'Seiten analysieren',
          t('progressGeneratingSummary')|| 'Zusammenfassung',
          t('progressGeneratingAudio')  || 'Voiceover',
          t('progressRenderingVideo')   || 'Video rendern',
          t('progressFinalizing')       || 'Speichern',
        ]
      : hasTranscription
      ? [
          t('progressUploading')        || 'Hochladen',
          t('progressTranscribing')     || 'Transkribieren',
          t('progressGeneratingScript') || 'Skript',
          t('progressGeneratingAudio')  || 'Voiceover',
          t('progressRenderingVideo')   || 'Video rendern',
          t('progressFinalizing')       || 'Speichern',
        ]
      : [
          t('progressUploading')        || 'Hochladen',
          t('progressProcessingMedia')  || 'Verarbeiten',
          t('progressGeneratingScript') || 'Skript',
          t('progressGeneratingAudio')  || 'Voiceover',
          t('progressRenderingVideo')   || 'Video rendern',
          t('progressFinalizing')       || 'Speichern',
        ];

    // Backend-label map und currentLabel werden nicht mehr angezeigt
    const displayProgress = Math.min(progressValue, 100);

    return (
      <div className="ec-step-content ec-processing" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '28px', padding: '32px 16px',
      }}>

        {/* Titel + Untertitel */}
        <div style={{ textAlign: 'center' }}>
          <h2 className="ec-title" style={{ margin: '0 0 6px' }}>
            {t('creatingElement') || 'Element wird erstellt …'}
          </h2>
          <p className="ec-subtitle" style={{ margin: 0 }}>
            {t('pleaseWait') || 'Das kann eine Minute dauern.'}
          </p>
        </div>

        {/* Kreis-Progress + Prozent */}
        <div style={{ position: 'relative', width: 108, height: 108 }}>
          <svg width="108" height="108" viewBox="0 0 108 108" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="54" cy="54" r="46" fill="none" stroke="#E5E7EB" strokeWidth="8" />
            <circle
              cx="54" cy="54" r="46"
              fill="none"
              stroke="#FF6407"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 46}`}
              strokeDashoffset={`${2 * Math.PI * 46 * (1 - displayProgress / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
            />
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.35rem', fontWeight: 700, color: '#111827',
            fontFamily: 'Inter, sans-serif',
          }}>
            {displayProgress}%
          </div>
        </div>

        {/* Stage-Punkte */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {stageLabels.map((_, i) => {
            const isDone    = i < progressStageIdx;
            const isCurrent = i === progressStageIdx;
            return (
              <div key={i} style={{
                width:  isCurrent ? 20 : 8,
                height: 8,
                borderRadius: 99,
                backgroundColor: isDone || isCurrent ? '#FF6407' : '#E5E7EB',
                opacity: isDone ? 0.5 : 1,
                transition: 'width 0.3s ease, background-color 0.3s ease',
              }} />
            );
          })}
        </div>
      </div>
    );
  };

  const renderStep6_Success = () => {
    const isPending = createdElementData?.pending_approval === true;

    const printerButtonConfig = {
      idle:       { label: t('printQRLabel') || '🖨️ QR-Label drucken',        style: { backgroundColor: '#6d28d9', color: '#fff' }, disabled: false },
      connecting: { label: t('printerConnecting') || '🔵 Verbinde Drucker...', style: { backgroundColor: '#a78bfa', color: '#fff' }, disabled: true  },
      printing:   { label: t('printerPrinting') || '⏳ Druckt...',             style: { backgroundColor: '#a78bfa', color: '#fff' }, disabled: true  },
      done:       { label: t('printerDone') || '✅ Gedruckt!',                 style: { backgroundColor: '#10b981', color: '#fff' }, disabled: false },
      error:      { label: t('printerError') || '❌ Fehler – Erneut versuchen',style: { backgroundColor: '#ef4444', color: '#fff' }, disabled: false },
    };
    const btnCfg = printerButtonConfig[printStatus] || printerButtonConfig.idle;

    console.log('[Niimbot] Step6 render – createdElementData:', JSON.stringify(createdElementData));
    console.log('[Niimbot] printStatus:', printStatus, '| printerConnected:', printerConnected);
    console.log('[ElementCreator] pending_approval:', isPending);

    // ── PENDING-VIEW: Supervisor-Modus aktiv — Video wartet auf Freigabe ──
    if (isPending) {
      return (
        <div className="ec-step-content ec-success-view" style={{ textAlign: 'center' }}>

          {/* Pending Icon */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: '#fef3c7', border: '2px solid #f59e0b',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 36, margin: '0 auto 20px',
          }}>
            ⏳
          </div>

          <h2 className="ec-title" style={{ textAlign: 'center', marginBottom: 12 }}>
            {t('elementPendingTitle') || 'Dein Video wird geprüft'}
          </h2>

          <p className="ec-subtitle" style={{
            textAlign: 'center', maxWidth: 320, margin: '0 auto 24px',
            lineHeight: 1.6, color: '#6b7280',
          }}>
            {t('elementPendingDesc') || 'Dein Video wurde hochgeladen und wird von einem Administrator geprüft.'}
          </p>

          {/* Info-Box */}
          <div style={{
            background: '#fffbeb', border: '1px solid #f59e0b',
            borderRadius: 10, padding: '14px 18px',
            maxWidth: 320, margin: '0 auto 28px',
            display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left',
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</span>
            <span style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
              {t('elementPendingHint') || 'Du erhältst eine Benachrichtigung, sobald dein Video freigegeben wurde.'}
            </span>
          </div>

          {/* Aktions-Buttons */}
          <div className="ec-success-actions">
            <button
              className="ec-btn ec-btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              {t('backToDashboard') || 'Back to Dashboard'}
            </button>
            <button
              className="ec-btn ec-btn-outline"
              onClick={() => window.location.reload()}
            >
              {t('create') || 'Weiteres Element erstellen'}
            </button>
          </div>
        </div>
      );
    }

    // ── NORMAL SUCCESS-VIEW ──────────────────────────────────────────────────
    return (
      <div className="ec-step-content ec-success-view">
        <div className="ec-success-icon">
          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <h2 className="ec-title" style={{textAlign: 'center'}}>
          {t('elementCreatedSuccess') || "Success!"}
        </h2>
        
        <p className="ec-subtitle" style={{textAlign: 'center'}}>
          {t('elementReadyDesc') || "Your element has been created and processed successfully."}
        </p>

        {/* --- QR CODE ANZEIGE --- */}
        {createdElementData && createdElementData.qr_direct_url && (
          <div className="ec-qr-container">
              <img 
                  src={getSecureMediaUrl(createdElementData.qr_direct_url)} 
                  alt="QR Code" 
                  className="ec-qr-image"
              />
              <span className="ec-qr-hint">
                  {t('scanToStart') || 'Scan to view on mobile'}
              </span>
          </div>
        )}

        {createdElementData && !createdElementData.qr_direct_url && (
          <div style={{marginBottom:'8px', padding:'8px', backgroundColor:'#fef3c7', borderRadius:'8px', fontSize:'0.75rem', color:'#92400e'}}>
            ⚠️ Kein qr_direct_url. API-Felder: {Object.keys(createdElementData).join(', ')}
          </div>
        )}

        <canvas ref={printCanvasRef} style={{ display: 'none' }} />

        <div className="ec-success-actions">

          <button
            className="ec-btn"
            onClick={printQRLabel}
            disabled={btnCfg.disabled}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              cursor: btnCfg.disabled ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.95rem',
              ...btnCfg.style
            }}
          >
            {btnCfg.label}
          </button>

          {printStatus === 'idle' && !printerConnected && (
            <p style={{ margin: '-8px 0 4px', fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center' }}>
              {t('bluetoothHint') || 'Verbindet per Bluetooth – Niimbot B1 in der Liste auswählen'}
            </p>
          )}
          {printStatus === 'error' && (
            <p style={{ margin: '-8px 0 4px', fontSize: '0.72rem', color: '#ef4444', textAlign: 'center' }}>
              {t('printerErrorHint') || 'Drucker eingeschaltet? Nur Chrome/Edge unterstützt Web Bluetooth.'}
            </p>
          )}
          {/* Video-Readiness Indikator + "Element ansehen"-Button */}
          {createdElementData?.videoUrl && videoReady !== true && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 10, padding: '12px 16px',
              display: 'flex', alignItems: 'center', gap: 12,
              marginBottom: 4,
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                border: '2px solid #d1d5db', borderTopColor: '#FF6B00',
                animation: 'spin 1s linear infinite',
              }} />
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                {t('videoProcessingHint') || 'Video wird gerade fertiggestellt. Warte noch, um es direkt anzusehen, oder arbeite einfach weiter.'}
              </span>
            </div>
          )}
          {createdElementData?.hash_id ? (
            <button
              className="ec-btn ec-btn-primary"
              disabled={createdElementData?.videoUrl ? videoReady !== true : false}
              style={{ opacity: (createdElementData?.videoUrl && videoReady !== true) ? 0.45 : 1,
                       cursor: (createdElementData?.videoUrl && videoReady !== true) ? 'not-allowed' : 'pointer' }}
              onClick={() => {
                if (createdElementData?.videoUrl && videoReady !== true) return;
                navigate(`/element-feed/${createdElementData.hash_id}`);
              }}
            >
              {(createdElementData?.videoUrl && videoReady !== true)
                ? (t('viewElement') || 'Element jetzt ansehen')
                : (t('viewElement') || 'Element jetzt ansehen')}
            </button>
          ) : (
            <button
              className="ec-btn ec-btn-primary"
              onClick={() => navigate('/my-courses')}
            >
              {t('goToMyCourses') || "Go to My Courses"}
            </button>
          )}
          
          <button 
            className="ec-btn ec-btn-secondary" 
            onClick={() => navigate('/dashboard')}
          >
            {t('backToDashboard') || "Back to Dashboard"}
          </button>
          
          <button 
            className="ec-btn ec-btn-outline" 
            onClick={() => window.location.reload()} 
          >
            {t('create') || "Create Another"} 
          </button>
        </div>
      </div>
    );
  };

  // --- MAIN RENDER ---

  if (isProcessing) {
    return (
      <GlobalLayout title={t('elementCreator') || "Element Creator"} showBackButton={false}>
        <div className="ec-container">
          {renderStep5_Processing()}
        </div>
      </GlobalLayout>
    );
  }

  return (
    <GlobalLayout 
      title={t('createElement') || "Create Element"} 
      showBackButton={true} 
      onBackClick={step === 1 ? () => navigate(-1) : handleBack}
    >
      <div className="ec-container">
        {/* Progress Indicator */}
        <div className="ec-progress-bar">
          <div className={`ec-progress-step ${step >= 1 ? 'active' : ''}`}></div>
          <div className={`ec-progress-step ${step >= 2 ? 'active' : ''}`}></div>
          <div className={`ec-progress-step ${step >= 3 ? 'active' : ''}`}></div>
          <div className={`ec-progress-step ${step >= 4 ? 'active' : ''}`}></div>
        </div>

        {/* Error Screen — ersetzt Step 4 komplett wenn Fehler aufgetreten */}
        {error && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: '20px', padding: '32px 16px',
            textAlign: 'center', minHeight: '60vh',
          }}>
            {/* Icon */}
            <div style={{ fontSize: 56 }}>
              {uploadErrorType === 'timeout' ? '⏳' : '⚠️'}
            </div>

            {/* Titel */}
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>
              {uploadErrorType === 'timeout'
                ? 'Verarbeitung dauert länger als erwartet'
                : 'Erstellung fehlgeschlagen'}
            </h2>

            {/* Beschreibung */}
            <p style={{ margin: 0, color: '#6B7280', fontSize: '0.95rem', maxWidth: 320, lineHeight: 1.5 }}>
              {uploadErrorType === 'timeout'
                ? 'Das Dokument wird noch verarbeitet. Bitte warte einen Moment und prüfe deine Inhalte — das Element wurde möglicherweise trotzdem erstellt.'
                : error}
            </p>

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 300 }}>
              {/* Retry */}
              <button
                className="ec-btn ec-btn-success"
                onClick={() => {
                  setError('');
                  setUploadErrorType(null);
                  setProgressValue(0);
                  setProgressStageIdx(0);
                  handleSubmit();
                }}
              >
                ↻ Erneut versuchen
              </button>

              {/* Meine Inhalte prüfen (bei Timeout sinnvoll) */}
              {uploadErrorType === 'timeout' && (
                <button
                  className="ec-btn ec-btn-secondary"
                  onClick={() => navigate('/my-courses')}
                >
                  Meine Inhalte prüfen
                </button>
              )}

              {/* Zurück zum Formular */}
              <button
                className="ec-btn ec-btn-secondary"
                onClick={() => {
                  setError('');
                  setUploadErrorType(null);
                  setStep(4);
                }}
              >
                ← Zurück zum Formular
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="ec-content-wrapper" style={{ display: error ? 'none' : undefined }}>
          {showScriptSelection ? (
              renderScriptSelection()
          ) : (
              <>
                {step === 1 && renderStep1_Name()}
                {step === 2 && renderStep2_Media()}
                {step === 3 && renderStep3_Description()}
                {step === 4 && renderStep4_Review()}
                {step === 6 && renderStep6_Success()}
              </>
          )}
        </div>

        {/* Footer Navigation - Nur anzeigen, wenn wir NICHT im Success Screen und NICHT im Error-Screen sind */}
        {step < 5 && !showScriptSelection && !error && (
          <div className="ec-footer">
            {step > 1 && (
              <button className="ec-btn ec-btn-secondary" onClick={handleBack}>
                {t('back') || 'Back'}
              </button>
            )}
            
            {step < 4 ? (
              <button className="ec-btn ec-btn-primary" onClick={handleNext}>
                {t('next') || 'Next'}
              </button>
            ) : (
              <button className="ec-btn ec-btn-success" onClick={handleSubmit}>
                {t('create') || 'Create Element'}
              </button>
            )}
          </div>
        )}
      </div>
    </GlobalLayout>
  );
};

export default ElementCreator;
